import { loginRequestSchema, type CurrentUser, type LoginRequest } from '@mfos/shared';
import { useMutation } from '@tanstack/react-query';
import { type SubmitEvent, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { ErrorNotice, SuccessNotice } from '../../components/ErrorNotice';
import { TextField } from '../../components/Field';
import { PublicLayout } from '../../components/PublicLayout';
import { safeNextPath } from '../../lib/safe-next';
import { validate, type FieldErrors } from '../../lib/validation';
import { useI18n } from '../../lib/i18n/runtime';
import { login, useCompleteSignIn } from './auth.api';
import { isPasswordUpdatedState } from './login-notice';
import { MfaStep, type MfaChallenge } from './MfaStep';

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const passwordUpdated = isPasswordUpdatedState(useLocation().state);
  const rawNext = searchParams.get('next');
  const next = safeNextPath(rawNext);
  const navigate = useNavigate();
  const completeSignIn = useCompleteSignIn();
  const { t } = useI18n();
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);

  function onAuthenticated(user: CurrentUser) {
    completeSignIn(user);
    void navigate(next, { replace: true });
  }

  return (
    <PublicLayout title={t('signIn')}>
      {passwordUpdated && challenge === null && (
        <SuccessNotice>{t('passwordUpdated')}</SuccessNotice>
      )}
      {rawNext !== null && challenge === null && (
        <p className="notice notice--info" role="status">
          <span aria-hidden="true">ℹ </span>
          {t('pleaseSignIn')}
        </p>
      )}
      {challenge === null ? (
        <PasswordStep onAuthenticated={onAuthenticated} onMfaRequired={setChallenge} />
      ) : (
        <MfaStep
          challenge={challenge}
          onAuthenticated={onAuthenticated}
          onCancel={() => {
            setChallenge(null);
          }}
        />
      )}
    </PublicLayout>
  );
}

function PasswordStep({
  onAuthenticated,
  onMfaRequired,
}: {
  onAuthenticated: (user: CurrentUser) => void;
  onMfaRequired: (challenge: MfaChallenge) => void;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const { t } = useI18n();
  const mutation = useMutation({
    mutationFn: (body: LoginRequest) => login(body),
    onSuccess: (result) => {
      if (result.status === 'AUTHENTICATED') {
        onAuthenticated(result.user);
      } else {
        onMfaRequired({ challengeToken: result.challengeToken, expiresAt: result.expiresAt });
      }
    },
  });

  function submit(event: SubmitEvent) {
    event.preventDefault();
    const result = validate(loginRequestSchema, { email, password });
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    mutation.mutate(result.data);
  }

  return (
    <form onSubmit={submit} noValidate className="stack">
      <TextField
        label={t('email')}
        type="email"
        autoComplete="username"
        value={email}
        onValueChange={setEmail}
        error={errors.email}
        required
      />
      <TextField
        label={t('password')}
        type="password"
        autoComplete="current-password"
        value={password}
        onValueChange={setPassword}
        error={errors.password !== undefined ? t('enterPassword') : undefined}
        required
      />
      <ErrorNotice error={mutation.error} />
      <button type="submit" className="button" disabled={mutation.isPending}>
        {mutation.isPending ? t('signingIn') : t('signIn')}
      </button>
      <ul className="link-list">
        <li>
          <Link to="/magic-link">{t('magicLink')}</Link>
        </li>
        <li>
          <Link to="/forgot-password">{t('forgotPassword')}</Link>
        </li>
      </ul>
    </form>
  );
}
